// @flow

// The history reducer stores the user's history stack as they navigate through
// a given tab/window's session. Every time the user does a soft nav (usually
// through ReactRouter via NavigateLink or withRouter manipulation), his or her
// history state is updated here.
//
// In addition to storing the location which encompases the main URL parts, we
// also store the route path that was matched to display the page. For instance,
// a location pathname may be '/pin/123' and the corresponding routePath would
// be '/pin/:id'.
//
// history was created to replace the location actions/reducer as a cleaner
// and more powerful API.
//
// history data is divided into a previous stack, current entry, and forward
// stack. The 0th entry of the previous stack is the oldest history entry. The
// 0th entry of the forward stack is the most recent future page (the last one
// shown before the user pressed the browser back button).
//
//        previous           current         forward
// [ '/' -> '/pin/123' ]    '/pin/456'    [ '/pin/789' ]
//    0          1                              0

import PropTypes from 'prop-types';
import type { Action } from 'app/common/redux/actions/types';
import type { HistoryAction, HistoryLocation } from 'app/common/redux/actions/history';

// Flow Types
export type HistoryEntry = {|
  location: HistoryLocation,
  routePath: string,
|};

export type CurrentHistoryEntry = {|
  ...HistoryEntry,
  action: HistoryAction,
|};

export type State = {|
  previous: Array<HistoryEntry>,
  current: ?CurrentHistoryEntry,
  forward: Array<HistoryEntry>,
|};

// PropTypes (keep in sync with Flow types)
export const HistoryLocationShape = PropTypes.shape({
  pathname: PropTypes.string.isRequired,
  hash: PropTypes.string,
  search: PropTypes.string,
  query: PropTypes.object,
  state: PropTypes.object,
});

export const HistoryEntryShape = PropTypes.shape({
  location: HistoryLocationShape.isRequired,
  routePath: PropTypes.string.isRequired,
});

export const HistoryStateShape = PropTypes.shape({
  previous: PropTypes.arrayOf(HistoryEntryShape).isRequired,
  current: HistoryEntryShape,
  forward: PropTypes.arrayOf(HistoryEntryShape).isRequired,
});

// Initial State
const initialState = { previous: [], current: null, forward: [] };

// Given a history entry, combine path and search to create a full path.
export const fullPath = (location: HistoryLocation) => {
  if (location && location.pathname) {
    return location.pathname + (location.search || '');
  } else {
    return '';
  }
};

// Main History Reducer
export default function history(state: State = initialState, action: Action): State {
  if (action.type === 'ROUTE_ENTER') {
    const { location, routePath } = action.payload;
    const newHistoryEntry = { location, routePath, action: action.payload.action };

    return {
      forward: [],
      current: newHistoryEntry,
      previous: [],
    };
  } else if (action.type === 'ROUTE_CHANGE') {
    const { action: method, location, routePath } = action.payload;
    const newHistoryEntry = { location, routePath, action: method };

    if (method === 'POP') {
      // The browser sends a pop action regardless of whether the user clicked
      // forward or back, so we need to use entry paths in order to
      // differentiate.
      if (
        state.forward.length > 0 &&
        fullPath(state.forward[0].location) === fullPath(newHistoryEntry.location)
      ) {
        // Forward button was pressed. Shift the 0th item off the forward stack
        // into current, and the current current (heh) into the previous stack.
        return {
          ...state,
          forward: state.forward.slice(1),
          current: { ...state.forward[0], action: method },
          previous: state.current
            ? [
                ...state.previous,
                { location: state.current.location, routePath: state.current.routePath },
              ]
            : state.previous,
        };
      } else {
        // Back button was pressed. Move the current entry into the forward stack
        // and pop the last previous entry into current.
        return {
          ...state,
          forward: state.current
            ? [
                { location: state.current.location, routePath: state.current.routePath },
                ...state.forward,
              ]
            : state.forward,
          current: { ...state.previous.slice(-1)[0], action: method },
          previous: state.previous.slice(0, -1),
        };
      }
    } else if (method === 'PUSH') {
      // Push the current item onto the previous stack and set this entry as the
      // new current. If there's any future items, clear them.
      return {
        ...state,
        forward: state.forward.length > 0 ? [] : state.forward,
        current: newHistoryEntry,
        previous: state.current
          ? [
              ...state.previous,
              { location: state.current.location, routePath: state.current.routePath },
            ]
          : state.previous,
      };
    } else if (method === 'REPLACE') {
      // Replace the last entry of previous with the new entry.
      return {
        ...state,
        current: newHistoryEntry,
      };
    }
  }

  return state;
}

// getPreviousHistoryEntry gets the previous page the user was on measured
// chronologically. In the case where the user pressed the back button on her
// browser, the previous page might actually be in the forward stack instead of
// the n-1th entry in the back stack.
export function getPreviousHistoryEntry(history: State): HistoryEntry | null {
  if (history.forward.length > 0) {
    return history.forward[0];
  }

  if (history.previous.length > 0) {
    return history.previous[history.previous.length - 1];
  }

  return null;
}
